import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { History, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resourceLabels, companyLabels, Company, Resource } from "@/hooks/rollenbeheer/types";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface AuditEntry {
  id: string;
  changed_by_user_name: string;
  company: string;
  role: string;
  resource: string;
  permission_type: string;
  old_value: boolean;
  new_value: boolean;
  created_at: string;
}

const permissionTypeLabels: Record<string, string> = {
  read: "Bekijken",
  write: "Bewerken",
  delete: "Verwijderen",
};

const roleLabels: Record<string, string> = {
  medewerker: "Medewerker",
  planner: "Planner",
  management: "Management",
};

const AuditLog = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["role-permission-audit-log"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("role_permission_audit_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as AuditEntry[];
    },
    enabled: isOpen,
  });

  // Group entries by timestamp (within 2 seconds = same save action)
  const grouped = groupByTimestamp(entries);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <History className="h-4 w-4" />
                Wijzigingsgeschiedenis
              </span>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Laden...</p>
            ) : entries.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nog geen wijzigingen vastgelegd.</p>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {grouped.map((group, i) => (
                  <div key={i} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {group[0].changed_by_user_name}
                      </span>
                      <span>
                        {new Date(group[0].created_at).toLocaleString("nl-NL", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {group.map((entry) => (
                        <div
                          key={entry.id}
                          className="flex items-center gap-2 text-sm flex-wrap"
                        >
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {companyLabels[entry.company as Company] ?? entry.company}
                          </Badge>
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {roleLabels[entry.role] ?? entry.role}
                          </Badge>
                          <span className="font-medium">
                            {resourceLabels[entry.resource as Resource] ?? entry.resource}
                          </span>
                          <span className="text-muted-foreground">›</span>
                          <span>{permissionTypeLabels[entry.permission_type] ?? entry.permission_type}</span>
                          <span>
                            {entry.new_value ? (
                              <Badge className="bg-green-100 text-green-800 text-[10px] px-1.5 py-0 hover:bg-green-100">
                                Aan
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 text-[10px] px-1.5 py-0 hover:bg-red-100">
                                Uit
                              </Badge>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

function groupByTimestamp(entries: AuditEntry[]): AuditEntry[][] {
  if (entries.length === 0) return [];
  
  const groups: AuditEntry[][] = [];
  let currentGroup: AuditEntry[] = [entries[0]];

  for (let i = 1; i < entries.length; i++) {
    const prev = new Date(entries[i - 1].created_at).getTime();
    const curr = new Date(entries[i].created_at).getTime();
    
    if (
      Math.abs(prev - curr) < 2000 &&
      entries[i].changed_by_user_name === entries[i - 1].changed_by_user_name
    ) {
      currentGroup.push(entries[i]);
    } else {
      groups.push(currentGroup);
      currentGroup = [entries[i]];
    }
  }
  groups.push(currentGroup);
  return groups;
}

export default AuditLog;
